import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  motion,
  useReducedMotion,
  useInView,
  useMotionValue,
  animate,
} from 'framer-motion';
import {
  Check,
  Wifi,
  AcUnit,
  LocalParking,
  LocalCafe,
  WaterDrop,
  Lock,
  Power,
  Videocam,
  AllInclusive,
  MeetingRoom,
  Groups,
  Print,
  LocalLibrary,
  Dashboard,
  Weekend,
  Chair,
  WbSunny,
  VolumeOff,
  ElectricalServices,
  Storefront,
  EventSeat,
  MenuBook,
} from '@mui/icons-material';

/**
 * Amenity → icon resolver. Each entry is matched if ANY of its keywords appears
 * as a substring of the (lowercased) amenity label, so custom variants like
 * "AC", "high-speed wifi" or "cctv camera" still resolve. Order matters —
 * more specific entries are listed first. Falls back to a check mark.
 */
const AMENITY_ICON_RULES = [
  { keywords: ['wifi', 'wi-fi', 'internet'], icon: <Wifi /> },
  { keywords: ['air condition', ' ac', 'a/c', 'cooling'], icon: <AcUnit /> },
  { keywords: ['parking'], icon: <LocalParking /> },
  { keywords: ['cafeteria', 'cafe', 'canteen', 'coffee', 'tea'], icon: <LocalCafe /> },
  { keywords: ['water', 'ro ', 'drinking'], icon: <WaterDrop /> },
  { keywords: ['locker', 'storage'], icon: <Lock /> },
  { keywords: ['power backup', 'backup', 'generator', 'inverter', 'ups'], icon: <Power /> },
  { keywords: ['cctv', 'camera', 'surveillance', 'security'], icon: <Videocam /> },
  { keywords: ['24/7', '24x7', '24 hour', 'always open'], icon: <AllInclusive /> },
  { keywords: ['discussion', 'group', 'meeting'], icon: <Groups /> },
  { keywords: ['study room', 'cabin', 'private room'], icon: <MeetingRoom /> },
  { keywords: ['print', 'photocopy', 'xerox', 'scan'], icon: <Print /> },
  { keywords: ['library', 'books', 'reading'], icon: <LocalLibrary /> },
  { keywords: ['whiteboard', 'board'], icon: <Dashboard /> },
  { keywords: ['bean bag'], icon: <Weekend /> },
  { keywords: ['ergonomic', 'chair', 'seating'], icon: <Chair /> },
  { keywords: ['natural light', 'sunlight', 'daylight', 'light'], icon: <WbSunny /> },
  { keywords: ['silent', 'quiet', 'noise-free', 'peaceful'], icon: <VolumeOff /> },
  { keywords: ['charging', 'charge point', 'power point', 'plug', 'socket', 'electric'], icon: <ElectricalServices /> },
  { keywords: ['stationery', 'store', 'shop'], icon: <Storefront /> },
  { keywords: ['seat', 'desk'], icon: <EventSeat /> },
  { keywords: ['notes', 'material'], icon: <MenuBook /> },
];

/** Resolve a React icon element for an amenity label. Always returns an element. */
export function getAmenityIcon(amenity) {
  if (!amenity || typeof amenity !== 'string') return <Check />;
  const key = ` ${amenity.toLowerCase().trim()} `;
  for (const rule of AMENITY_ICON_RULES) {
    if (rule.keywords.some((kw) => key.includes(kw))) return rule.icon;
  }
  return <Check />;
}

// Plan period labels
export const PLAN_PERIOD = {
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
  half_yearly: '6 months',
};

/** "per day" / "per week" / "per month" / "per 6 months" — "" for unknown */
export function planPeriodLabel(type) {
  if (!type) return '';
  const period = PLAN_PERIOD[type];
  return period ? `per ${period}` : '';
}

/** ₹1,500 formatted via en-IN locale. Returns null when input is null/undefined/NaN. */
export function formatPrice(rupees) {
  if (rupees === null || rupees === undefined) return null;
  const num = Number(rupees);
  if (Number.isNaN(num)) return null;
  return `₹${num.toLocaleString('en-IN')}`;
}

/** null/undefined → "Unlimited hours"; integer → "6 hrs/day" */
export function hoursLabel(hours_per_day) {
  if (hours_per_day === null || hours_per_day === undefined) return 'Unlimited hours';
  return `${hours_per_day} hrs/day`;
}

/**
 * Discounted first-month price in rupees (rounded).
 * Returns null when pct is falsy or 0.
 * price_cents is RUPEES — never multiply by 100.
 */
export function discountedFirstPrice(price_cents, pct) {
  if (!pct || !price_cents) return null;
  const rupees = Number(price_cents);
  const discount = Number(pct);
  if (Number.isNaN(rupees) || Number.isNaN(discount) || discount <= 0) return null;
  return Math.round(rupees * (1 - discount / 100));
}

/** "9:00 AM – 10:00 PM"; null if either side is missing. */
export function formatHours(open_time, close_time) {
  if (!open_time || !close_time) return null;
  const fmt = (t) => {
    try {
      // Handle "HH:MM" or "HH:MM:SS" strings
      const [h, m] = t.split(':').map(Number);
      const d = new Date(2000, 0, 1, h, m);
      return d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
    } catch {
      return t; // passthrough if parse fails
    }
  };
  return `${fmt(open_time)} – ${fmt(close_time)}`;
}

/**
 * Human-readable operational days string.
 * Handles array (join) or string (passthrough). Null-safe.
 */
export function formatOperationalDays(operational_days) {
  if (!operational_days) return null;
  if (Array.isArray(operational_days)) {
    if (operational_days.length === 0) return null;
    return operational_days.join(', ');
  }
  if (typeof operational_days === 'string' && operational_days.trim()) {
    return operational_days.trim();
  }
  return null;
}

/**
 * Full address string, de-duplicating against library.address.
 * Falls back to library.address if nothing better can be built.
 */
export function formatFullAddress(library) {
  if (!library) return '';
  const parts = [
    library.address_line1,
    library.address_line2,
    library.locality,
    library.city,
    library.state,
    library.pincode,
  ].filter(Boolean);

  if (parts.length === 0) return library.address || '';

  // De-dupe: if built string is essentially the same as library.address, use library.address
  const built = parts.join(', ');
  if (library.address && library.address.length >= built.length) return library.address;
  return built;
}

/**
 * Small presentational feature row: icon + caption text.
 * Restyleable via props so each theme can pass its own color.
 */
export function PlanFeature({ icon, children, color, sx }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        mt: 0.5,
        ...sx,
      }}
    >
      {icon && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: color || 'text.secondary',
            fontSize: 16,
            '& .MuiSvgIcon-root': { fontSize: 16 },
          }}
        >
          {icon}
        </Box>
      )}
      <Typography variant="caption" sx={{ color: color || 'text.secondary', lineHeight: 1.4 }}>
        {children}
      </Typography>
    </Box>
  );
}

/** Title-case a plan_type string ("half_yearly" → "Half Yearly") */
export function titleCasePlanType(plan_type) {
  if (!plan_type) return '';
  return plan_type
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ─────────────────────────────────────────────────────────────────────────────
// Motion toolkit — GPU-friendly (transform + opacity only), prefers-reduced-
// motion aware. Import what you need; nothing below has side-effects.
// ─────────────────────────────────────────────────────────────────────────────

// ─── 1. Reveal ───────────────────────────────────────────────────────────────
/**
 * Scroll-triggered fade + slide-up wrapper.
 *
 * Props
 *   children  — content to reveal
 *   delay     — stagger offset in seconds (default 0)
 *   y         — initial vertical offset in px (default 24)
 *   duration  — animation duration in seconds (default 0.5)
 *   once      — only animate once (default true)
 *   style     — passed straight through to the motion.div
 *   ...rest   — any other motion.div props (className, onClick, …)
 *
 * Reduced-motion: renders children immediately at full opacity with no
 * transform so content is never hidden from assistive tech or slow devices.
 */
export function Reveal({
  children,
  delay = 0,
  y = 24,
  duration = 0.5,
  once = true,
  style,
  ...rest
}) {
  const shouldReduce = useReducedMotion();

  if (shouldReduce) {
    return (
      <div style={{ opacity: 1, ...style }} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, margin: '-80px' }}
      transition={{ duration, delay, ease: 'easeOut' }}
      style={style}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

// ─── 2. staggerContainer / staggerItem variants ───────────────────────────────
/**
 * Framer Motion variants for orchestrated stagger lists.
 *
 * Usage:
 *   <motion.div
 *     variants={staggerContainer}
 *     initial="hidden"
 *     whileInView="show"
 *     viewport={{ once: true }}
 *   >
 *     {items.map(item => (
 *       <motion.div key={item.id} variants={staggerItem}>
 *         {item.label}
 *       </motion.div>
 *     ))}
 *   </motion.div>
 *
 * Prefer the <MotionList> / <MotionItem> pair below when you need built-in
 * prefers-reduced-motion handling without extra boilerplate.
 */
export const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
};

export const staggerItem = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { ease: 'easeOut', duration: 0.4 },
  },
};

// ─── 3. AnimatedNumber ────────────────────────────────────────────────────────
/**
 * Count-up number that animates when scrolled into view (once).
 *
 * Props
 *   value    — target number (if non-finite string like "42 / 60", renders as-is)
 *   duration — count-up duration in seconds (default 1.2)
 *   format   — optional fn(n: number) → string; default rounds + en-IN locale
 *   prefix   — string prepended to the formatted value (e.g. "₹")
 *   suffix   — string appended to the formatted value (e.g. "+")
 *   style    — passed to the wrapping <span>
 *
 * Reduced-motion: renders the final formatted value immediately, no animation.
 * Non-numeric value: rendered as a plain string, no animation.
 */
export function AnimatedNumber({
  value,
  duration = 1.2,
  format,
  prefix = '',
  suffix = '',
  style,
}) {
  const shouldReduce = useReducedMotion();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const motionVal = useMotionValue(0);
  const [display, setDisplay] = useState(null);

  const isNumeric = typeof value === 'number' && Number.isFinite(value);

  // Stable formatter reference — memoised so useEffect dependency arrays are stable
  const fmt = useMemo(
    () => format || ((n) => Math.round(n).toLocaleString('en-IN')),
    [format],
  );

  // Initialise display to final value so SSR / no-IntersectionObserver is safe
  useEffect(() => {
    if (!isNumeric) return;
    setDisplay(fmt(value));
  }, [isNumeric, value, fmt]);

  useEffect(() => {
    if (!isNumeric) return;
    if (shouldReduce) {
      setDisplay(fmt(value));
      return;
    }
    if (!isInView) return;

    const controls = animate(motionVal, value, {
      duration,
      ease: 'easeOut',
      onUpdate: (latest) => setDisplay(fmt(latest)),
      onComplete: () => setDisplay(fmt(value)),
    });

    return () => controls.stop();
  }, [isInView, shouldReduce, isNumeric, value, duration, fmt, motionVal]);

  if (!isNumeric) {
    return (
      <span style={style}>
        {prefix}
        {value}
        {suffix}
      </span>
    );
  }

  return (
    <span ref={ref} style={style}>
      {prefix}
      {display !== null ? display : fmt(value)}
      {suffix}
    </span>
  );
}

// ─── 4. MotionList / MotionItem ───────────────────────────────────────────────
/**
 * Reduced-motion-safe staggered list pair.
 *
 * MotionList — wraps a group; triggers the stagger when scrolled into view.
 *   Props: style, className, ...rest (passed to the wrapper element)
 *
 * MotionItem — each child item in the staggered list.
 *   Props: style, className, ...rest (passed to the item element)
 *
 * When prefers-reduced-motion is set, both render plain divs so content is
 * immediately visible and no transforms are applied.
 *
 * Usage:
 *   <MotionList>
 *     {items.map(item => (
 *       <MotionItem key={item.id}>{item.label}</MotionItem>
 *     ))}
 *   </MotionList>
 */
export function MotionList({ children, style, className, ...rest }) {
  const shouldReduce = useReducedMotion();

  if (shouldReduce) {
    return (
      <div style={style} className={className} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, margin: '-60px' }}
      style={style}
      className={className}
      {...rest}
    >
      {children}
    </motion.div>
  );
}

export function MotionItem({ children, style, className, ...rest }) {
  const shouldReduce = useReducedMotion();

  if (shouldReduce) {
    return (
      <div style={style} className={className} {...rest}>
        {children}
      </div>
    );
  }

  return (
    <motion.div variants={staggerItem} style={style} className={className} {...rest}>
      {children}
    </motion.div>
  );
}
